<template>
    <app-layout v-if="isPageReady">
        <common-content-spacer height="medium">
            <common-content-spacer height="small">
                <common-breadcrumbs :breadcrumbs="pageBreadcrumbs"></common-breadcrumbs>

                <common-page-header :heading="pageTitle"></common-page-header>
            </common-content-spacer>

            <common-page-split class="page-split__referrals">
                <template #aside>
                    <common-content-spacer height="medium">
                        <referrals-show-details
                            :referral="referral"
                            :type="$route.params.type"
                            @referral-changed="changeReferralData($event)"
                        ></referrals-show-details>
                        <div v-if="referral.proposedAvailabilitySlots?.length">
                            <common-page-header
                                :heading="` Your proposed time slots:`"
                                size="page-plain-header"
                            ></common-page-header>
                            <referral-time-slots-preview :groupSlotsByDate="groupSlotsByDate"></referral-time-slots-preview>
                        </div>
                    </common-content-spacer>
                </template>

                <template #main>
                    <div
                        v-bind="contentProps"
                        @referral-changed="changeReferralData($event)"
                        @insurance-auth-declined="changeReferralData({ isInsuranceJustDeclined: true })"
                        @change-app-date="changeAppDate"
                        @change-referral-status="changeReferralStatus"
                    ></div>
                    <div v-if="referral.status === ReferralStatusEnum.RESCHEDULE_REQUESTED_BY_PATIENT && isReferred">
                        <referrals-new-schedule
                            ref="referralsNewSchedule"
                            :physician="physician"
                            :practice="{}"
                            :location="{}"
                            :selectedSlotsByDate="selectedSlotsByDate"
                            @selectSlot="slotChanged($event)"
                        ></referrals-new-schedule>
                    </div>
                    <div v-if="showReferralsDetails">
                        <common-content-spacer height="small">
                            <referrals-status :referral="referral" :isInline="true"></referrals-status>
                            <referral-details :referral="referral" :isReferralPool="true"></referral-details>
                        </common-content-spacer>
                    </div>
                </template>
            </common-page-split>
        </common-content-spacer>
    </app-layout>
</template>

<script>
    import { composeDisplayName } from 'components/common/CommonUserName'
    import PageWithTitleMixin from 'mixins/PageWithTitleMixin'
    import PageWithElementsMixin from 'mixins/PageWithElementsMixin'
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import AppLayout from 'components/layouts/AppLayout'
    import ReferralsAPI from 'api/ReferralsAPI'
    import ReferralStatusMixin from 'mixins/ReferralStatusMixin'
    import ReferralStatusEnum from '@/enums/ReferralStatusEnum'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import ReferralsShowDetails from 'components/page-elements/referrals/ReferralsShowDetails'
    import ScreenSizeDetectionMixin from 'mixins/ScreenSizeDetectionMixin'
    import ReferralPathTypeEnum from '@/enums/ReferralPathTypeEnum'
    import DemoReferral from 'services/DemoReferral'
    import dayjs from 'dayjs'
    import ReferralsNewSchedule from 'components/page-elements/referrals/ReferralsNewSchedule'
    import ReferralTimeSlotsPreview from 'components/page-elements/referrals/ReferralTimeSlotsPreview.vue'
    import ReferralDetails from 'components/page-elements/referrals/ReferralDetails'
    import ReferralsStatus from 'components/page-elements/ReferralsStatus'

    export default {
        mixins: [PageWithTitleMixin, PageWithElementsMixin, PageWithDataMixin, ReferralStatusMixin, ScreenSizeDetectionMixin],

        components: {
            AppLayout,
            ReferralsShowDetails,
            ReferralsNewSchedule,
            ReferralTimeSlotsPreview,
            ReferralDetails,
            ReferralsStatus,
        },

        data() {
            return {
                referral: {},
                physician: this.$auth.getUser(),
                ReferralStatusEnum: ReferralStatusEnum,
                selectedSlotsByDate: {},
            }
        },

        computed: {
            isReferred() {
                return this.$route.params.type === ReferralPathTypeEnum.RECEIVED
            },

            groupSlotsByDate() {
                return this.groupSlotsByDateForUser(this.referral.proposedAvailabilitySlots)
            },

            showReferralsDetails() {
                return (
                    this.referral.status === ReferralStatusEnum.TIME_SLOTS_PROPOSED ||
                    (!this.isReferred && this.referral.status === ReferralStatusEnum.RESCHEDULE_REQUESTED_BY_PATIENT)
                )
            },

            contentProps() {
                if (
                    this.referral.status === 'CREATED' ||
                    this.referral.status === 'RESCHEDULE_REQUESTED' ||
                    this.referral.status === 'RESCHEDULE_AVAILABILITY_CHANGE' ||
                    (this.isReferralError(this.referral) && !this.isReferred) ||
                    (this.referral.status === ReferralStatusEnum.PATIENT_VISIT_PENDING &&
                        this.$route.query.successfully_rescheduled == 1)
                ) {
                    return {
                        is: 'referrals-show-pending',
                        referral: this.referral,
                    }
                } else if (
                    this.referral.status === ReferralStatusEnum.PATIENT_VISIT_PENDING ||
                    this.referral.status === 'CONSULTATION_NOTES_PENDING' ||
                    this.referral.status === 'INSURANCE_AUTH_PENDING' ||
                    this.referral.status === 'COMPLETED' ||
                    (this.isReferralError(this.referral) && this.isReferred)
                ) {
                    return {
                        is: 'referrals-view-details',
                        referral: this.referral,
                    }
                }

                return null
            },

            physicianName() {
                const user = this.referral[this.$route.params.type === this.isReferred ? 'toUser' : 'fromUser'] || {}
                const name = composeDisplayName(user)
                return user.designation ? `${name}, ${user.designation}` : name
            },

            pageTitle() {
                return this.$t(`pages.referrals.show.titles.${this.$route.params.type}`, {
                    name: this.getReferralTypeName,
                    designation: this.referral?.primaryCarePhysician?.designation || '',
                })
            },

            getReferralTypeName() {
                if (this.$route.params.type === this.isReferred) {
                    if (this.referral.fromPractice?.practiceType === PracticeTypeEnum.PHYSICIAN) {
                        return this.physicianName
                    } else {
                        return this.referral.fromPractice?.name
                    }
                } else {
                    if (this.referral.practice && this.referral.practice.practiceType === PracticeTypeEnum.PHYSICIAN) {
                        return this.physicianName
                    } else if (this.referral.practice && this.referral.practice.practiceType === PracticeTypeEnum.ANCILLARY) {
                        return this.referral.practice.name
                    } else {
                        return ''
                    }
                }
            },

            pageBreadcrumbs() {
                return [
                    {
                        label: this.$t('pages.referrals.index.breadcrumbs.referrals'),
                        route: { name: `referrals.${this.$route.params.type}.referrals` },
                    },
                    {
                        label: this.$t(`pages.referrals.index.breadcrumbs.${this.$route.params.type}`),
                        route: { name: `referrals.${this.$route.params.type}.referrals` },
                    },
                    {
                        label: this.$t(`pages.referrals.show.breadcrumbs.${this.$route.params.type}`, {
                            name: this.getReferralTypeName,
                        }),
                    },
                ]
            },
        },

        methods: {
            changeReferralData(data) {
                if (this.$auth.isUserPracticeDemo()) {
                    localStorage.setItem('disclaimerAceppted', true)
                    this.referral = DemoReferral.getConfirmedReferral()
                    return false
                }
                this.referral = { ...this.referral, ...data }
            },

            changeAppDate() {
                if (this.$auth.isUserPracticeDemo()) {
                    this.referral.appointmentDateTime = dayjs(new Date()).subtract(1, 'day').format('YYYY-MM-DDTHH:mm:ss')
                }
            },

            groupSlotsByDateForUser(proposedTimeSlots) {
                const groupedSlots = proposedTimeSlots.reduce((acc, slot) => {
                    if (!acc[slot.date]) {
                        acc[slot.date] = []
                    }
                    acc[slot.date].push(slot)
                    return acc
                }, {})

                Object.keys(groupedSlots).forEach((date) => {
                    groupedSlots[date].sort((a, b) =>
                        dayjs(a.startTime, 'HH:mm').isBefore(dayjs(b.startTime, 'HH:mm')) ? -1 : 1,
                    )
                })

                return groupedSlots
            },

            changeReferralStatus() {
                this.referral.status = 'COMPLETED'
                this.$router.push(`/referrals/received/${this.referral.id}/add-report`)
            },
        },
        watch: {
            $route() {
                ReferralsAPI.get(
                    this.$route.params.id,
                    this.$auth.getActiveMember()?.id ? this.$auth.getActiveMember()?.id : this.$auth.getUser().id,
                )
                    .then((res) => (this.referral = res))
                    .catch(() => {
                        this.$router.go(-1)
                    })
            },
        },
        mounted() {
            if (this.$auth.isUserPracticeDemo()) {
                this.referral = DemoReferral.getPendingReferral()
                return
            } else {
                ReferralsAPI.get(
                    this.$route.params.id,
                    this.$auth.getActiveMember()?.id ? this.$auth.getActiveMember()?.id : this.$auth.getUser().id,
                )
                    .then((res) => (this.referral = res))
                    .catch(() => {
                        this.$router.go(-1)
                    })
            }
        },
    }
</script>
